































































































































































import TheLayoutPortal from "@/layouts/TheLayoutPortal.vue";
import { handleError } from "@/lib/utility/handleError";
import PartnerFallbackMixin from "@/mixins/PartnerFallbackMixin.vue";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import PartnerConversationMixin from "@/mixins/PartnerConversationSendMixin";
import { ConversationChannelEnum } from "@/lib/enum/conversation-channel.enum";
import {
  conversationChannelIconMap,
  ConversationDetail,
  conversationStatusColorMap,
  ConversationStatusEnum
} from "@/models/conversation.entity";
import { IPartnerMessage } from "@/models/partner-message.entity";
import { ConversationModule } from "@/store/modules/conversation.store";
import { PartnerModule } from "@/store/modules/partner";
import { mixins } from "vue-class-component";
import { Component, Watch } from "vue-property-decorator";
import LatestEntriesCardEmpty from "@/components/cards/LatestEntriesCardEmpty.vue";
import PartnerConversationChatMessage from "@/components/partner/PartnerConversationChatMessage.vue";
import WssSubscriptionMixin from "@/mixins/WssSubscriptionMixin";
import { IWssSubscriptions, WssEventNameEnum } from "@/lib/wss/wss-subscription.types";
import { NotificationSoundPlayer } from "@/lib/utility/notificationSound";

@Component({
  components: {
    TheLayoutPortal,
    LatestEntriesCardEmpty,
    PartnerConversationChatMessage
  }
})
export default class PartnerConversationDetailView extends mixins(
  PartnerFallbackMixin,
  PermissionMixin,
  PartnerConversationMixin,
  WssSubscriptionMixin
) {
  ConversationStatusEnum = ConversationStatusEnum;

  loading = true;
  isSending = false;
  newMessage = "";

  $refs!: {
    chatContainer: HTMLElement;
  };

  private notificationPlayer = new NotificationSoundPlayer({ volume: 0.1 });

  get partner() {
    return PartnerModule.partner;
  }

  get conversation(): ConversationDetail | null {
    return ConversationModule.selectedConversation;
  }

  get messages(): IPartnerMessage[] {
    return this.conversation?.messages ?? [];
  }

  /** Messages sorted by date ascending (oldest first, newest at bottom) */
  get sortedMessages(): IPartnerMessage[] {
    return [...this.messages].reverse();
  }

  get isDetailLoading(): boolean {
    return ConversationModule.isDetailLoading;
  }

  get isLoadingMore(): boolean {
    return ConversationModule.isLoadingMore;
  }

  get hasMoreMessages(): boolean {
    return ConversationModule.hasMoreMessages;
  }

  get channelIcon(): string {
    if (!this.conversation) return "mdi-message-outline";
    return conversationChannelIconMap.get(this.conversation.channel) ?? "mdi-message-outline";
  }

  get statusColor(): string {
    if (!this.conversation) return "grey";
    return conversationStatusColorMap.get(this.conversation.status) ?? "grey";
  }

  get canSend(): boolean {
    return !!this.newMessage.trim() && !this.isSending && this.conversation?.status === ConversationStatusEnum.ACTIVE;
  }

  get isWhatsappChannel(): boolean {
    return this.conversation?.channel === ConversationChannelEnum.WHATSAPP;
  }

  get isWhatsappSessionExpired(): boolean {
    if (!this.isWhatsappChannel || !this.conversation) return false;
    // If the backend explicitly says the session is not active
    if (!this.conversation.isWhatsappSessionActive) return true;
    // Fallback: check if whatsappSessionExpiresAt is in the past
    if (this.conversation.whatsappSessionExpiresAt) {
      return new Date(this.conversation.whatsappSessionExpiresAt) < new Date();
    }
    return false;
  }

  get isWhatsappSessionExpiringSoon(): boolean {
    if (!this.isWhatsappChannel || !this.conversation?.whatsappSessionExpiresAt) return false;
    if (this.isWhatsappSessionExpired) return false;
    const expiresAt = new Date(this.conversation.whatsappSessionExpiresAt);
    const hoursUntilExpiry = (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60);
    return hoursUntilExpiry <= 4; // Warn if less than 4 hours remaining
  }

  get whatsappSessionExpiresAtReadable(): string {
    if (!this.conversation?.whatsappSessionExpiresAt) return "";
    const date = new Date(this.conversation.whatsappSessionExpiresAt);
    return date.toLocaleString("de-DE");
  }

  get whatsappExpiresInText(): string | null {
    if (!this.conversation?.whatsappSessionExpiresAt) return null;
    if (this.isWhatsappSessionExpired) return null;
    const expiresAt = new Date(this.conversation.whatsappSessionExpiresAt);
    const diffMs = expiresAt.getTime() - Date.now();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const remainingMinutes = diffMinutes % 60;
    if (diffHours > 0) {
      return this.$t("components.PartnerConversationView.expiresInHoursMinutes", {
        hours: diffHours,
        minutes: remainingMinutes
      }) as string;
    }
    return this.$t("components.PartnerConversationView.expiresInMinutes", { minutes: diffMinutes }) as string;
  }

  /**
   * WebSocket subscriptions for real-time updates.
   * Refreshes conversation when new messages arrive for the selected conversation.
   */
  get wssSubscriptions(): IWssSubscriptions {
    return {
      subscriptions: [
        {
          event: WssEventNameEnum.MESSAGE_CREATE,
          filter: () => {
            return true;
          },
          handler: () => {
            void this.refreshCurrentConversation().then(() => {
              this.notificationPlayer.play();
            });
          },
          debounce: 500
        }
      ]
    };
  }

  @Watch("messages.length")
  onMessagesChanged() {
    this.scrollToBottom();
  }

  async mounted() {
    await this.trySetByRouteOrDefault();

    try {
      await ConversationModule.fetchConversationDetail({
        partnerId: this.$route.params.partnerId,
        conversationId: this.$route.params.conversationId
      });
      this.$nextTick(() => this.scrollToBottom());
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
    }
  }

  beforeDestroy() {
    this.notificationPlayer.dispose();
  }

  async loadMoreMessages() {
    if (!this.conversation) return;

    const scrollContainer = this.$refs.chatContainer;
    const previousScrollHeight = scrollContainer?.scrollHeight ?? 0;

    try {
      await ConversationModule.loadMoreMessages({
        partnerId: this.$route.params.partnerId,
        conversationId: this.conversation.id
      });

      // Maintain scroll position after loading more messages
      this.$nextTick(() => {
        if (scrollContainer) {
          const newScrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = newScrollHeight - previousScrollHeight;
        }
      });
    } catch (e) {
      handleError(e);
    }
  }

  /**
   * Refresh the currently selected conversation.
   * Called by the WSS subscription when new messages arrive.
   */
  async refreshCurrentConversation() {
    if (!this.conversation) return;

    try {
      await ConversationModule.fetchConversationDetail({
        partnerId: this.$route.params.partnerId,
        conversationId: this.conversation.id
      });
    } catch (e) {
      handleError(e);
    }
  }

  async onEnterKey(e: KeyboardEvent) {
    if (e.shiftKey) return;
    e.preventDefault();
    await this.sendMessage();
  }

  async sendMessage() {
    if (!this.canSend || !this.conversation) return;

    this.isSending = true;
    try {
      await this.sendConversationMessage(this.$route.params.partnerId, this.conversation, this.newMessage);
      this.newMessage = "";
    } catch (e) {
      handleError(e);
    } finally {
      this.isSending = false;
    }

    this.refreshCurrentConversation();
  }

  scrollToBottom() {
    this.$nextTick(() => {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  handleBack() {
    if (!this.conversation) {
      return;
    }

    this.$router.push({
      name: "PartnerConversationView",
      params: { partnerId: this.conversation.partnerId }
    });
    return;
  }
}
